import { localizedStrings } from '@core/strings'
import React from 'react'
import { MenuItem, Tooltip, Button } from '@common/material'
import { Box, Flex, Text } from '@fundamentals'
import { alpha } from '@mui/material'
import { ProjectPlanPermits } from '@core/entities/project/ProjectPlanPermits'
import { CanvasState, DEFAULT_COLOR } from '@core/entities/skia/CanvasState'
import { AllShapes } from '@core/entities/skia'
import { Palette } from '@core/theme/color-palette-siteassist'

type ProjectPlanZonesSidebarWithPinsProps = {
  planPermits?: ProjectPlanPermits
  canvasState: CanvasState
  readOnly?: boolean
  multipleSelection?: boolean
  onStateChange?: (state: CanvasState) => void
}

export const ProjectPlanZonesSidebarWithPins: React.FC<
  ProjectPlanZonesSidebarWithPinsProps
> = ({
  planPermits,
  canvasState,
  readOnly = false,
  multipleSelection = false,
  onStateChange,
}) => {
  const selectedShapes = canvasState.getSelectedShapes()
  const selectedZoneNames =
    planPermits?.permitStats?.getSelectedAreaNames(
      selectedShapes.map((shape) => shape.groupId),
    ) ?? []
  const duplicateNames = selectedZoneNames.filter(
    (name, index) => selectedZoneNames.indexOf(name) !== index,
  )

  const handleSelectAll = () => {
    if (readOnly) return
    const newState = canvasState.selectAllShapes()
    onStateChange?.(newState)
  }

  const handleShapeSelect = (shape: AllShapes) => {
    if (readOnly) return

    const newState = canvasState.selectShape(shape, multipleSelection)
    onStateChange?.(newState)
  }

  const areAllShapesSelected =
    selectedShapes.length === canvasState.shapes.length

  return (
    <Box
      sx={{
        position: 'relative',
        width: '18.5rem',
        transition: 'width 0.2s ease-out',
        boxShadow: `0px 6px 10px ${alpha('#000', 0.14)}`,
        overflowY: 'scroll',
        overflowX: 'hidden',
        bgcolor: 'background.paper',
        zIndex: 1,
      }}
    >
      <Flex
        pl={1.4}
        py={1}
        position='sticky'
        top={0}
        zIndex={1}
        flexDirection='column'
        bgcolor={Palette.Common.White}
      >
        <Flex
          px={2.6}
          ml={-1.4}
          alignItems='center'
          justifyContent='space-between'
        >
          <Text fontSize={15}>
            {localizedStrings.selectedFrom(
              selectedShapes.length,
              canvasState.shapes.length,
            )}
          </Text>

          <Button
            variant='outlined'
            color='primary'
            size='small'
            disabled={readOnly}
            onClick={handleSelectAll}
          >
            {areAllShapesSelected
              ? localizedStrings.deselectAll
              : localizedStrings.selectAll}
          </Button>
        </Flex>
      </Flex>

      {canvasState.shapes.map((shape) => {
        return (
          <MenuItem
            key={shape.id}
            sx={{
              width: '100%',
              '&.Mui-disabled': {
                opacity: 0.8,
              },
              py: 0,
            }}
            selected={shape.isSelected}
            disabled={readOnly}
            onClick={() => handleShapeSelect(shape)}
          >
            <Box
              mr='13px'
              ml='6px'
              height={60}
              width={2}
              borderRadius={0.5}
              bgcolor={shape.isActive ? 'primary.main' : DEFAULT_COLOR}
              sx={{
                opacity: shape.isSelected ? 1 : 0.6,
              }}
            />

            <Flex flexDirection='column'>
              <Tooltip
                title={
                  duplicateNames.includes(shape.name) &&
                  localizedStrings.duplicateZoneDescription
                }
                arrow
              >
                <Text
                  sx={{ opacity: 1, width: '100px' }}
                  color={shape.isSelected ? 'primary' : undefined}
                >
                  {shape.name}
                </Text>
              </Tooltip>

              <Text
                sx={{
                  opacity: 0.6,
                }}
              >
                {`${
                  planPermits?.permitStats?.getAreaTotalElements(shape.id) ?? 0
                } ${localizedStrings.permits}`}
              </Text>
            </Flex>
          </MenuItem>
        )
      })}
    </Box>
  )
}
